import React, { useEffect, useRef, useState } from 'react';
import './MarketplaceSellerProfile.scss';
import { useDispatch } from 'react-redux';
import { addNotificationFlagToDelete } from '../../slices/notification-flags/notification-flags.slice';
import { NotificationFlagsCategories } from '../../models/NotificationFlags/NotificationFlagsCategories';
import { NotificationFlag } from '../NotificationFlag/NotificationFlag';
import { RefreshNotificationFlagsExceptPrefix } from '../../slices/notification-flags/refresh-notification-flags-except-prefix';
import ButtonLoader from '../Layout/Buttons/ButtonLoader';
import { GetStatesByRegion } from '../../slices/locations-slice/thunks/get-states-by-region.thunk';
import SellerLegalDocsInfoInputForm, {
    SellerLegalDocsInfoInputFormRef,
} from './SellerLegalDocsInfoInputForm/SellerLegalDocsInfoInputForm';
import SellerMyProfileForm, {
    SellerMyProfileFormRef,
} from './SellerMyProfileForm/SellerMyProfileForm';
import PreviewProfileModal from './PreviewProfileModal/PreviewProfileModal';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { useStateSelector } from '../../store/selectors';
import { MarketplaceSellerProfile } from '../../slices/profiles-data-slice/models/marketplace-seller-profile';
import { useCurrentMarketplaceParty } from '../../custom-hooks/useCurrentMarketplaceParty';
import { TimelineParty } from '../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import { TimelineActionType } from '../../slices/marketplace/timeline/models/timeline-action-type.model';
import { TimelineEventStatus } from '../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { GetTimelineById } from '../../slices/marketplace/timeline/thunks/get-timeline-by-id.thunk';
import createNotification from '../../utils/createNotification';
import { USER_ROLES } from '../../utils/constants';

enum SellerProfileTab {
    MyProfile = 1,
    LegalDocsInfo = 2,
}

const SellerProfileTabs = [
    {
        id: SellerProfileTab.MyProfile,
        content: 'My Profile',
    },
    {
        id: SellerProfileTab.LegalDocsInfo,
        content: 'Legal Docs Info',
    },
] as const;

interface MarketplaceSellerProfileComponentProps {
    inReadonly?: boolean;
}

export const MarketplaceSellerProfileComponent = (
    props: MarketplaceSellerProfileComponentProps
) => {
    const [activeTab, setActiveTab] = useState<SellerProfileTab>(
        SellerProfileTab.MyProfile
    );
    const sellerMyProfileFormRef = useRef<SellerMyProfileFormRef>(null);
    const sellerLegalDocsInfoFormRef =
        useRef<SellerLegalDocsInfoInputFormRef>(null);
    const isSubmitProfileFlowRef = useRef(false);
    const initialNotificationsReadRef = useRef(false);
    const readCurrentTabNotificationsRef = useRef<() => void>(() => {});
    const readTabsRef = useRef<Set<SellerProfileTab>>(new Set());
    const dispatch = useDispatch() as any;

    const [isSaving, setIsSaving] = useState(false);
    const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [isSubmitProfileConfirmationVisible, setIsSubmitProfileConfirmationVisible] =
        useState(false);

    const axios = useStateSelector((state) => state.core.axios);
    const summary = useStateSelector((state) => state.userSummary.summary);
    const sellerProfile = useStateSelector(
        (state) => state.profilesData.selectedPofile
    ) as MarketplaceSellerProfile;

    const currentEngagement = useStateSelector(
        (state) => state.timelines.currentTimeline
    );
    const notificationFlags = useStateSelector(
        (s) => s.notificationFlags.flags
    );
    const currentParty = useCurrentMarketplaceParty(currentEngagement?.id);
    const isBroker = currentParty === TimelineParty.Broker;
    const activeSellerProfileEvent = currentEngagement?.events?.find(
        (e) => e.linkedAction?.actionType === TimelineActionType.SellerProfile
    );
    const showSubmitProfileButton =
        !!activeSellerProfileEvent &&
        activeSellerProfileEvent.status !== TimelineEventStatus.Completed &&
        !isBroker;

    const completeEventAndRefresh = () => {
        if (!activeSellerProfileEvent || !currentEngagement?.id) return;
        axios
            .put(
                `/api/Marketplace/Engagements/${currentEngagement.id}/CompleteEvent/${activeSellerProfileEvent.id}`
            )
            .then(() => {
                createNotification('Profile submitted successfully.', 'success');
                dispatch(
                    GetTimelineById({
                        id: currentEngagement.id,
                        isTemplate: false,
                    })
                );
            })
            .catch(() => {
                createNotification(
                    'An error occurred while submitting profile. Please try again.',
                    'error'
                );
            })
            .finally(() => {
                setIsSubmittingProfile(false);
                isSubmitProfileFlowRef.current = false;
            });
    };

    const handleSubmitProfile = () => {
        if (!activeSellerProfileEvent || !currentEngagement?.id) return;
        isSubmitProfileFlowRef.current = true;
        setIsSubmittingProfile(true);
        sellerMyProfileFormRef.current?.handleSubmit();
    };

    const handleMyProfileSaveSuccess = () => {
        if (isSubmitProfileFlowRef.current) {
            sellerLegalDocsInfoFormRef.current?.handleSubmit();
        }
    };

    const handleMyProfileSaveError = () => {
        setIsSubmittingProfile(false);
        isSubmitProfileFlowRef.current = false;
    };

    const getTabNotificationPrefix = (tabId: SellerProfileTab): string | null => {
        if (!currentEngagement?.id) return null;
        const base = `${NotificationFlagsCategories.MpCode}.${currentEngagement.id}.${NotificationFlagsCategories.SellerProfileUpdatedCode}`;
        switch (tabId) {
            case SellerProfileTab.MyProfile:
                return `${base}.PracticeProfiles`;
            case SellerProfileTab.LegalDocsInfo:
                return `${base}.SellerDocsInfo`;
            default:
                return null;
        }
    };

    const readTabNotifications = (tabId: SellerProfileTab) => {
        if (tabId === SellerProfileTab.MyProfile) {
            const practiceId = sellerMyProfileFormRef.current?.getSelectedPracticeId();
            if (!practiceId || !currentEngagement?.id) return;
            const base = `${NotificationFlagsCategories.MpCode}.${currentEngagement.id}.${NotificationFlagsCategories.SellerProfileUpdatedCode}`;
            const prefix = `${base}.PracticeProfiles.${practiceId}`;
            notificationFlags
                .filter((f) => f.eventCode.toLowerCase().startsWith(prefix.toLowerCase()))
                .forEach((f) => dispatch(addNotificationFlagToDelete(f)));
            return;
        }
        const prefix = getTabNotificationPrefix(tabId);
        if (!prefix) return;
        notificationFlags
            .filter((f) => f.eventCode.toLowerCase().startsWith(prefix.toLowerCase()))
            .forEach((f) =>
                dispatch(addNotificationFlagToDelete({ ...f, refreshAfterRemoval: true }))
            );
    };

    const handleTabChange = (tabId: SellerProfileTab) => {
        readTabNotifications(activeTab);
        readTabsRef.current.add(activeTab);
        setActiveTab(tabId);
        const newTabPrefix = getTabNotificationPrefix(tabId);
        const isFirstVisit = !readTabsRef.current.has(tabId);
        dispatch(RefreshNotificationFlagsExceptPrefix({ prefixesToIgnore: newTabPrefix && isFirstVisit ? [newTabPrefix] : [] }));
    };

    readCurrentTabNotificationsRef.current = () => readTabNotifications(activeTab);

    useEffect(() => {
        dispatch(GetStatesByRegion());
    }, []);

    useEffect(() => {
        return () => {
            readCurrentTabNotificationsRef.current();
        };
    }, []);

    useEffect(() => {
        initialNotificationsReadRef.current = false;
        readTabsRef.current = new Set();
    }, [currentEngagement?.id]);

    useEffect(() => {
        if (!currentEngagement?.id || initialNotificationsReadRef.current) return;
        const practiceId = sellerMyProfileFormRef.current?.getSelectedPracticeId();
        if (!practiceId || !notificationFlags.length) return;
        initialNotificationsReadRef.current = true;
        readTabNotifications(SellerProfileTab.MyProfile);
    }, [currentEngagement?.id, notificationFlags]);

    const renderTabContent = (): React.ReactNode => {
        const isEditable = !props.inReadonly;

        return (
            <>
                <div
                    style={{
                        display:
                            activeTab === SellerProfileTab.MyProfile
                                ? 'block'
                                : 'none',
                    }}>
                    <SellerMyProfileForm
                        ref={sellerMyProfileFormRef}
                        setIsSaving={setIsSaving}
                        isEditable={isEditable}
                        isAdmin={summary?.roleId === USER_ROLES.ADMIN}
                        timelineId={currentEngagement?.id}
                        isBroker={isBroker}
                        onSaveSuccess={
                            showSubmitProfileButton
                                ? handleMyProfileSaveSuccess
                                : undefined
                        }
                        onSaveError={
                            showSubmitProfileButton
                                ? handleMyProfileSaveError
                                : undefined
                        }
                    />
                </div>
                <div
                    style={{
                        display:
                            activeTab === SellerProfileTab.LegalDocsInfo
                                ? 'block'
                                : 'none',
                    }}>
                    <SellerLegalDocsInfoInputForm
                        ref={sellerLegalDocsInfoFormRef}
                        setIsSaving={setIsSaving}
                        isEditable={isEditable}
                        timelineId={currentEngagement?.id}
                        onSaveSuccess={
                            showSubmitProfileButton
                                ? completeEventAndRefresh
                                : undefined
                        }
                    />
                </div>
            </>
        );
    };

    const renderActionButtons = (): React.ReactNode => (
        <div className="actions-buttons mt-4">
            {showSubmitProfileButton && (
                <ButtonLoader
                    className="btn btn-primary submit-button"
                    isLoading={isSubmittingProfile}
                    loaderButtonText={''}
                    disabled={isSaving || isSubmittingProfile}
                    buttonText={'Submit Profile'}
                    onClick={() => setIsSubmitProfileConfirmationVisible(true)}
                />
            )}
            <ButtonLoader
                className="btn btn-primary submit-button"
                isLoading={isSaving}
                loaderButtonText={''}
                disabled={!!props.inReadonly || isSaving}
                buttonText={'Save Changes'}
                onClick={() => {
                        if (
                            activeTab === SellerProfileTab.MyProfile &&
                            sellerMyProfileFormRef.current
                        ) {
                            sellerMyProfileFormRef.current.handleSubmit();
                        } else if (
                            activeTab === SellerProfileTab.LegalDocsInfo &&
                            sellerLegalDocsInfoFormRef.current
                        ) {
                            sellerLegalDocsInfoFormRef.current.handleSubmit();
                        }
                    }}
                />
            <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                    setIsPreviewModalVisible(true);
                }}>
                Preview Profile
            </button>
        </div>
    );

    return (
        <div className="marketplace-seller-profile">
            <div className="body_inner_content marketplace-seller-tabs-container">
                <div className="RRT__container">
                    <div className="RRT__tabs">
                        {SellerProfileTabs.map((item) => (
                            <div
                                key={item.id}
                                className={
                                    activeTab === item.id
                                        ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                        : 'RRT__tab'
                                }
                                onClick={() => handleTabChange(item.id)}>
                                {item.content}
                                {getTabNotificationPrefix(item.id) && (
                                    <NotificationFlag
                                        flagClassName="notification-flag-wrapper"
                                        eventCode={getTabNotificationPrefix(item.id)!}
                                        includePrefix={true}
                                        doNotRemove={true}
                                    />
                                )}
                            </div>
                        ))}
                        {renderActionButtons()}
                    </div>
                </div>
                {renderTabContent()}
            </div>
            {isPreviewModalVisible && (
                <PreviewProfileModal
                    isVisible={isPreviewModalVisible}
                    onClose={() => setIsPreviewModalVisible(false)}
                    profileData={sellerProfile}
                />
            )}
            {isSubmitProfileConfirmationVisible && (
                <ConfirmationModal
                    header="Submit Your Profile"
                    isVisible={isSubmitProfileConfirmationVisible}
                    setIsVisible={setIsSubmitProfileConfirmationVisible}
                    confirmationText="By submitting your profile, you confirm that all information has been completed accurately and understand that the fully disclosed view will be visible to prospective buyers after the execution of the ID NDA."
                    onConfirm={handleSubmitProfile}
                    nextButtonText="Submit Profile"
                    cancelButtonText="Continue Editing"
                />
            )}
        </div>
    );
};
